/**
 * PendingApproval — Block-screen shown to vendor users whose admin review
 * has not resolved yet. Surfaces the current approval state ('pending' |
 * 'rejected' | 'setup' for not-yet-submitted) plus the submitted business
 * details + an "Edit Profile" path so they can update their submission
 * while it sits in the admin queue.
 */
import React from 'react';
import { Card, Button, Badge } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import SEOHead from '@components/common/SEOHead';

const STATUS_CONFIG = {
  pending: {
    icon: 'bi-hourglass-split',
    iconColor: '#F59E0B',
    badge: { bg: 'warning', text: 'dark', label: 'Pending Approval' },
    title: 'Your account is under review',
    message: 'Thanks for setting up your vendor profile! Our team is reviewing your submission and will activate your account shortly. You\'ll get an email as soon as you\'re approved — typically within 1 business day.',
  },
  rejected: {
    icon: 'bi-x-octagon',
    iconColor: '#DC2626',
    badge: { bg: 'danger', text: 'light', label: 'Application Declined' },
    title: 'Your application was not approved',
    message: 'Unfortunately, your vendor application did not meet our current requirements. If you believe this was a mistake or you\'d like to discuss it further, please reach out to our support team.',
  },
  setup: {
    icon: 'bi-pencil-square',
    iconColor: '#7C3AED',
    badge: { bg: 'info', text: 'light', label: 'Setup Required' },
    title: 'Finish setting up your vendor profile',
    // Neutral copy that works regardless of the platform's
    // auto-approve setting: when auto-approve is OFF the submission
    // still goes into the admin queue (see the 'pending' screen
    // afterwards); when ON, submission activates the portal instantly.
    // The previous "1 business day" promise was misleading on
    // auto-approve installs — the activation is immediate, not delayed.
    message: 'Add your business name, category, and a few key details to activate your portal. This is the only setup step — once you submit, your account becomes live (or enters review, depending on platform settings).',
  },
};

const PendingApproval = ({ status = 'pending', vendor = null }) => {
  const navigate = useNavigate();
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;

  return (
    <>
      <SEOHead title="Pending Approval" noIndex />
      <div className="d-flex justify-content-center align-items-start py-5 px-3">
        <Card className="border-0 shadow-sm" style={{ maxWidth: 560, width: '100%', borderRadius: 16 }}>
          <Card.Body className="p-4 p-md-5 text-center">
            <div
              className="d-inline-flex align-items-center justify-content-center mb-3"
              style={{ width: 72, height: 72, borderRadius: '50%', background: `${cfg.iconColor}1A` }}
            >
              <i className={`bi ${cfg.icon}`} style={{ color: cfg.iconColor, fontSize: 32 }} />
            </div>
            <Badge bg={cfg.badge.bg} text={cfg.badge.text} pill className="mb-3 px-3 py-2">
              {cfg.badge.label}
            </Badge>
            <h4 className="fw-bold mb-2" style={{ color: '#1D1D44' }}>{cfg.title}</h4>
            <p className="text-muted mb-4">{cfg.message}</p>

            {vendor && (
              <div className="text-start bg-light rounded p-3 mb-4">
                <div className="small text-muted text-uppercase mb-2" style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                  Submitted Details
                </div>
                {vendor.businessName && (
                  <div className="d-flex justify-content-between py-1">
                    <span className="text-muted small">Business</span>
                    <span className="fw-semibold small">{vendor.businessName}</span>
                  </div>
                )}
                {vendor.category && (
                  <div className="d-flex justify-content-between py-1">
                    <span className="text-muted small">Category</span>
                    <span className="fw-semibold small text-capitalize">{vendor.category}</span>
                  </div>
                )}
                {vendor.email && (
                  <div className="d-flex justify-content-between py-1">
                    <span className="text-muted small">Contact</span>
                    <span className="fw-semibold small">{vendor.email}</span>
                  </div>
                )}
              </div>
            )}

            <div className="d-flex flex-column flex-sm-row gap-2 justify-content-center">
              {status !== 'rejected' && (
                <Button
                  style={{ background: '#9B30FF', border: 'none' }}
                  onClick={() => navigate('/vendor/profile')}
                >
                  <i className="bi bi-pencil me-1" />
                  {status === 'setup' ? 'Set Up Profile' : 'Edit Profile'}
                </Button>
              )}
              <Button
                variant="outline-secondary"
                href="mailto:support@vowsera.com"
              >
                <i className="bi bi-envelope me-1" />
                Contact Support
              </Button>
            </div>
          </Card.Body>
        </Card>
      </div>
    </>
  );
};

export default PendingApproval;
